import { useStatsigClient } from '@statsig/react-bindings'
import { getErrorMessage } from '@traba/utils'
import { useEffect, useState } from 'react'

import type { CompanyFeatureFlagResult } from '../types/useCompanyFeatureFlag'

// Statsig's client user context is process-wide singleton state. If two
// instances of this hook run concurrently their updateUser/restore awaits
// can interleave so one instance's checkGate observes another instance's
// company user (wrong gate result) or one instance's restore is overwritten
// (leaking the company context). Serialize through a module-level chain
// so each check captures `originalUser` only after any in-flight restore.
let statsigGateQueue: Promise<unknown> = Promise.resolve()

/**
 * Checks a Statsig gate for the given company by temporarily updating
 * the Statsig user context with companyId in userID, custom, and customIDs.
 * Always restores the original user context.
 *
 * Returns { isEnabled, isLoading } so consumers can avoid flashing a redirect
 * during the brief window before Statsig resolves.
 */
export function useCompanyFeatureFlag(
  companyId: string | undefined,
  gateKey: string,
): CompanyFeatureFlagResult {
  const { client } = useStatsigClient()
  const [isEnabled, setIsEnabled] = useState(false)
  // isLoading is derived from resolvedKey so it correctly reports true when
  // either input changes (covers undefined→defined companyId transitions).
  const [resolvedKey, setResolvedKey] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!companyId) {
      setIsEnabled(false)
      setResolvedKey(undefined)
      return
    }

    let isCancelled = false

    const enqueueCheck = () => {
      const prior = statsigGateQueue
      const ourTurn = (async () => {
        await prior.catch(() => {})
        const originalUser = client.getContext().user
        try {
          await client.updateUserAsync({
            userID: companyId,
            custom: { companyId },
            customIDs: { companyId },
          })

          if (!isCancelled) {
            setIsEnabled(client.checkGate(gateKey))
          }
        } catch (error: unknown) {
          console.error(
            'Failed to check company feature flag:',
            getErrorMessage(error),
          )
          if (!isCancelled) {
            setIsEnabled(false)
          }
        } finally {
          try {
            await client.updateUserAsync(originalUser)
          } catch (restoreError: unknown) {
            console.error(
              'Failed to restore Statsig user context:',
              getErrorMessage(restoreError),
            )
          }
          if (!isCancelled) {
            setResolvedKey(`${companyId}:${gateKey}`)
          }
        }
      })()
      statsigGateQueue = ourTurn.catch(() => {})
    }

    enqueueCheck()

    return () => {
      isCancelled = true
    }
  }, [client, companyId, gateKey])

  // Falsy companyId (undefined or '') matches the effect's `!companyId`
  // short-circuit so isLoading can't get stuck true for empty input.
  const isLoading =
    Boolean(companyId) && resolvedKey !== `${companyId}:${gateKey}`

  return { isEnabled, isLoading }
}
